import CronGenerator from "cron-time-generator2";

import type { ScheduleExpression } from "src/components/schedule/types";

// Day keys are stored lowercase; visual-cron schedules are always in UTC.
export const VISUAL_CRON_DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type VisualCronDay = (typeof VISUAL_CRON_DAY_ORDER)[number];

const CRON_DAY_BY_NAME: Record<VisualCronDay, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/** Selected days in calendar order, ignoring any key that is not a weekday. */
export function selectedVisualCronDays(
  expression: ScheduleExpression,
): VisualCronDay[] {
  return VISUAL_CRON_DAY_ORDER.filter((day) => expression.days?.[day]);
}

/**
 * Converts visual-cron rows into cron expressions, skipping any row that
 * cannot produce one (no day selected, or a missing/malformed time).
 *
 * Days must reach cron-time-generator2 as numbers. Its day-name validator
 * rejects every name that maps to 0, so passing "sunday" throws.
 */
export function visualCronToCronExpressions(
  expressions: ScheduleExpression[],
): string[] {
  const result: string[] = [];

  for (const expression of expressions) {
    const days = selectedVisualCronDays(expression).map(
      (day) => CRON_DAY_BY_NAME[day],
    );
    if (days.length === 0) continue;

    const [hoursRaw, minutesRaw] = (expression.time ?? "").split(":");
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) continue;

    result.push(CronGenerator.onSpecificDaysAt(days, hours, minutes));
  }

  return result;
}
